'use client'

import React, { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import Image from 'next/image'
import { usePathname, useRouter } from 'next/navigation'
import { ArrowRight, CheckCircle2, ChevronRight, CirclePercent, Clock, Coins, Copy, Download, ExternalLink, Gift, HandCoins, Home, Info, Loader2, MapPin, Package, Phone, Printer, Receipt, RotateCcw, ShieldCheck, Smartphone, Truck, Zap } from 'lucide-react'
import { bankList, formatDateNormal, formatDateTimeLong, formatDateTimeNormal, formatImageURL, formatNumber, formatPrice } from '@/utils/util'
import { uploadURL } from '@/lib/customFields'
import { STATUS_LABELS, TERMINAL_STATUSES, PAYMENT_METHOD_LABELS, DIGITAL_DOWNLOAD_STATUSES, calcPriceBreakdown, formatOrderAddress, getCustomerName, getProductPricing, isCustomFieldVisible, buildDigitalDownloadUrl, downloadDigitalFile, bookloDisplayStatus, showsReadyStep, calcDepositBreakdown, getPayableAmount, hasLiveInvoice, resolveOrderStatus, DEPOSIT_LABELS, getOmniwayQrImage } from '@/utils/order'
import DepositBalanceCard from '@/components/blocks/DepositBalanceCard'
import { navigateUrl } from '@/utils/client-request'
import { getVendorPhones } from '@/utils/vendorPhones'
import { usePaymentChecker, useInAppBrowser, IN_APP_BROWSER_LABELS, type PaymentEndpoint } from '@/lib/commerce/hooks'
import { toast } from '@/lib/toast'
import QRCodeImport from 'react-qr-code'
import qpayLogo from '@/assets/qpay.svg'
import pocketzeroLogo from '@/assets/pocketzero.svg'
import storepayLogo from '@/assets/storepay.png'
import lendmnLogo from '@/assets/lendmn.png'
import omniwayLogo from '@/assets/omniway.png'

const QRCode = QRCodeImport as unknown as React.FC<{ value: string, size?: number, fgColor?: string, bgColor?: string, style?: React.CSSProperties }>

type StatusToneKey = 'amber' | 'blue' | 'emerald' | 'rose' | 'muted'

const TONES: Record<StatusToneKey, { bg: string, color: string, border: string }> = {
  amber: { bg: 'rgba(245,158,11,0.14)', color: '#fbbf24', border: 'rgba(245,158,11,0.3)' },
  blue: { bg: 'rgba(59,130,246,0.14)', color: '#93c5fd', border: 'rgba(59,130,246,0.3)' },
  emerald: { bg: 'rgba(16,185,129,0.14)', color: '#34d399', border: 'rgba(16,185,129,0.3)' },
  rose: { bg: 'rgba(244,63,94,0.14)', color: '#fda4af', border: 'rgba(244,63,94,0.3)' },
  muted: { bg: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.7)', border: 'rgba(255,255,255,0.13)' },
}

// productImage stores one bucket object name, or an array of them for multi-select fields.
// Buyer-supplied, so anything uploadURL cannot place in our bucket is dropped.
function customFieldImages(value: any): string[] {
  return (Array.isArray(value) ? value : [value]).map(image => uploadURL(image)).filter((url): url is string => !!url)
}

function renderCustomFieldValue(field: any, dateFormatter: (v: any) => string): React.ReactNode {
  if (field.type === 'checkbox') return field.value === 'true' || field.value === true ? 'Тийм' : 'Үгүй'
  if (field.type === 'date') return field.value ? dateFormatter(field.value) : '—'
  if (field.type === 'productImage') {
    const images = customFieldImages(field.value)
    if (images.length === 0) return '—'
    return (
      <span className="inline-flex flex-wrap gap-1 align-middle">
        {images.map((image: string) => (
          <a key={image} href={image} target="_blank" rel="noopener noreferrer">
            <img src={image} alt="" className="w-10 h-10 rounded-lg object-cover" style={{ background: 'rgba(255,255,255,0.06)' }} />
          </a>
        ))}
      </span>
    )
  }
  if (field.type === 'file') {
    const url = uploadURL(field.value)
    if (!url) return '—'
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 underline transition-opacity hover:opacity-80"
        style={{ color: '#93c5fd' }}
      >
        Файл харах
        <ExternalLink className="h-3 w-3" />
      </a>
    )
  }
  return field.value || '—'
}

const STATUS_TONE_MAP: Record<string, StatusToneKey> = {
  pending: 'amber',
  paid: 'blue',
  ready: 'blue',
  shipped: 'blue',
  delivered: 'emerald',
  canceled: 'rose',
  refunded: 'muted',
  partiallyRefunded: 'rose',
  deliveryFailed: 'rose',
}

const STATUS_ORDER = ['pending', 'paid', 'ready', 'shipped', 'delivered']
// Pay-after-delivery reverses the last two steps: the goods arrive, then the money.
// 'settled' is keyed off paidAt because order.status never passes through 'paid'.
const ON_DELIVERY_STATUS_ORDER = ['pending', 'ready', 'shipped', 'delivered', 'settled']
const STEP_LABELS: Record<string, string> = {
  pending: 'Захиалсан',
  paid: 'Төлбөр баталгаажсан',
  ready: 'Бэлэн боллоо',
  shipped: 'Хүргэлтэнд гарсан',
  delivered: 'Хүргэгдсэн',
  settled: 'Төлбөр баталгаажсан',
}
const STEP_ICONS: Record<string, any> = {
  pending: Receipt,
  paid: CheckCircle2,
  ready: Package,
  shipped: Truck,
  delivered: Home,
  settled: CheckCircle2,
}

function formatStep(value: any) {
  if (!value) return null
  const d = new Date(value)
  if (isNaN(d.getTime())) return null
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(d.getMonth() + 1)}.${pad(d.getDate())} · ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function CopyField({ label, value, mono }: { label: string, value: string, mono?: boolean }) {
  const [flashed, setFlashed] = useState(false)
  const handle = () => {
    if (!value) return toast.error('Хуулах утга байхгүй байна')
    navigator.clipboard?.writeText(value)
    setFlashed(true)
    setTimeout(() => setFlashed(false), 900)
  }
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-[0.18em] mb-1.5" style={{ color: '#a8a8b3' }}>{label}</div>
      <button
        type="button"
        onClick={handle}
        className="group w-full flex items-center gap-2 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-white/5"
        style={{
          background: flashed ? 'rgba(52,211,153,0.1)' : 'rgba(0,0,0,0.3)',
          border: `1px solid ${flashed ? 'rgba(52,211,153,0.4)' : 'rgba(255,255,255,0.07)'}`,
        }}
      >
        <span className={`flex-1 text-[13px] text-white ${mono ? 'font-mono tabular-nums break-all' : 'font-medium'}`}>{value}</span>
        <span className="inline-flex items-center gap-1 text-[11px] font-semibold shrink-0" style={{ color: flashed ? '#34d399' : 'rgba(255,255,255,0.55)' }}>
          {flashed ? <><CheckCircle2 className="h-3 w-3" />Хуулсан</> : <><Copy className="h-3 w-3" />Хуулах</>}
        </span>
      </button>
    </div>
  )
}

// `image`: value is a ready-made QR image (data URI) from the provider, not a string to encode.
function QRFrame({ value, accent, logoSrc, size = 200, image }: { value: string, accent: string, logoSrc?: any, size?: number, image?: boolean }) {
  if (!value) {
    return (
      <div className="rounded-lg animate-pulse" style={{ width: size + 32, height: size + 32, background: 'rgba(255,255,255,0.04)' }} />
    )
  }
  const corners = [
    { pos: { top: 0, left: 0 }, border: 'border-t-2 border-l-2 rounded-tl-xl' },
    { pos: { top: 0, right: 0 }, border: 'border-t-2 border-r-2 rounded-tr-xl' },
    { pos: { bottom: 0, left: 0 }, border: 'border-b-2 border-l-2 rounded-bl-xl' },
    { pos: { bottom: 0, right: 0 }, border: 'border-b-2 border-r-2 rounded-br-xl' },
  ]
  return (
    <div className="relative inline-block" style={{ width: size + 32, height: size + 32 }}>
      <div className="absolute inset-0 pointer-events-none">
        {corners.map((c, i) => (
          <div key={i} className={'absolute w-5 h-5 ' + c.border} style={{ ...c.pos, borderColor: accent }} />
        ))}
      </div>
      <div className="absolute inset-2 rounded-lg bg-white p-3">
        {image
          ? <img src={value} alt="QR" className="w-full h-full object-contain" />
          : <QRCode value={value} size={size - 8} fgColor="#0a0a0c" bgColor="#ffffff" style={{ width: '100%', height: '100%' }} />}
        {logoSrc && (
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-11 h-11 rounded-md bg-white border border-zinc-200 shadow grid place-items-center p-1">
            <Image src={logoSrc} alt="" className="max-w-full max-h-full object-contain" />
          </div>
        )}
      </div>
    </div>
  )
}

function MethodCardHeader({ logo, title, subtitle, badge }: { logo: any, title: string, subtitle?: string, badge?: React.ReactNode }) {
  return (
    <div className="px-5 pt-5 pb-4 flex items-center gap-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
      <div className="w-11 h-11 rounded-lg grid place-items-center shrink-0 p-2 overflow-hidden" style={{ background: 'white', border: '1px solid rgba(255,255,255,0.13)' }}>{logo}</div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-[0.18em] font-semibold" style={{ color: '#a8a8b3' }}>Төлбөрийн арга</div>
        <div className="text-[15px] font-semibold text-white truncate" style={{ fontFamily: 'Gilroy, Inter, sans-serif' }}>
          {title}{subtitle ? <span className="font-normal" style={{ color: 'rgba(255,255,255,0.55)' }}> · {subtitle}</span> : null}
        </div>
      </div>
      {badge}
    </div>
  )
}

function NumberedSteps({ steps, accent }: { steps: string[], accent: string }) {
  return (
    <ol className="space-y-2 text-[13px]" style={{ color: 'rgba(255,255,255,0.85)' }}>
      {steps.map((s, i) => (
        <li key={i} className="flex gap-2.5">
          <span
            className="shrink-0 w-5 h-5 rounded-full grid place-items-center text-[10px] font-bold mt-0.5 text-white"
            style={{ background: accent }}
          >
            {i + 1}
          </span>
          <span>{s}</span>
        </li>
      ))}
    </ol>
  )
}

function CheckPaymentButton({ loading, success, onClick }: { loading: boolean, success: boolean, onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      disabled={loading}
      className="w-full h-11 rounded-full text-[13px] font-semibold inline-flex items-center justify-center gap-2 transition-opacity disabled:opacity-60"
      style={{ background: 'white', color: '#0a0a0c' }}
    >
      {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Шалгаж байна…</> : success ? <><CheckCircle2 className="h-4 w-4" /> Төлбөр төлөгдсөн байна</> : <>Төлбөр шалгах <ArrowRight className="h-3.5 w-3.5" /></>}
    </button>
  )
}

function DigitalDownloadAction({ product, order, vendor }: { product: any, order: any, vendor: any }) {
  const [downloading, setDownloading] = useState(false)
  const productData = product.product || product
  const variant = product.variant
  if (!productData?.isDigital || !variant?.digitalFile || !DIGITAL_DOWNLOAD_STATUSES.has(order?.status)) return null
  const downloadStatus = (order?.digitalDownloadStatus || []).find((s: any) => s?.variantId === variant.id) || null
  const isExpired = !!downloadStatus?.expired
  const isLimitHit = !!downloadStatus && downloadStatus.downloadsRemaining === 0 && !isExpired
  const downloadUrl = !isExpired && !isLimitHit ? buildDigitalDownloadUrl(order, vendor, variant.id) : null
  return (
    <div className="mt-2 flex flex-col gap-1.5">
      {downloadUrl ? (
        <button onClick={async () => { setDownloading(true); await downloadDigitalFile(downloadUrl); setDownloading(false) }} disabled={downloading} className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-[11.5px] font-semibold cursor-pointer transition-opacity hover:opacity-90 disabled:opacity-60 disabled:pointer-events-none w-fit" style={{ background: 'white', color: '#0a0a0c' }}>
          {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} {downloading ? 'Татаж байна...' : 'Файл татах'}
        </button>
      ) : (
        <div className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[11.5px] font-medium w-fit" style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.55)' }}>
          <Download className="w-3 h-3" /> {isExpired ? 'Татах хугацаа дууссан' : 'Татах хязгаар хэтэрсэн'}
        </div>
      )}
      {downloadStatus && !isExpired && (
        <div className="text-[11px] tabular-nums" style={{ color: 'rgba(255,255,255,0.55)' }}>
          Үлдсэн: <span className="font-semibold text-white">{downloadStatus.downloadsRemaining}/{downloadStatus.downloadsMax}</span>
          {downloadStatus.expiresAt && <span> · {formatDateNormal(new Date(downloadStatus.expiresAt))} хүртэл</span>}
        </div>
      )}
    </div>
  )
}

function DigitalDownloadRow({ order, vendor }: { order: any, vendor: any }) {
  const [downloading, setDownloading] = useState(false)
  const digitalProducts = (order?.products || []).filter((p: any) => {
    const productData = p.product || p
    return productData?.isDigital && p.variant?.digitalFile
  })
  if (!digitalProducts.length || !DIGITAL_DOWNLOAD_STATUSES.has(order?.status)) return null
  const single = digitalProducts.length === 1
  const firstStatus = single
    ? (order?.digitalDownloadStatus || []).find((s: any) => s?.variantId === digitalProducts[0].variant.id) || null
    : null
  const firstUnavailable = !!firstStatus && (firstStatus.expired || firstStatus.downloadsRemaining === 0)
  const firstUrl = single && !firstUnavailable ? buildDigitalDownloadUrl(order, vendor, digitalProducts[0].variant.id) : null
  return (
    <div className="rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center gap-4" style={{ background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.3)' }}>
      <div className="flex items-start sm:items-center gap-3 flex-1 min-w-0">
        <div className="w-10 h-10 rounded-full inline-flex items-center justify-center shrink-0" style={{ background: 'rgba(59,130,246,0.18)', color: '#93c5fd', border: '1px solid rgba(59,130,246,0.3)' }}>
          <Download className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[14px] font-semibold text-white">Дижитал бүтээгдэхүүн татах</div>
          <div className="text-[12.5px] mt-0.5" style={{ color: 'rgba(255,255,255,0.6)' }}>
            {single ? 'Доорх товчлуурыг дарж файлаа татаж авна уу.' : `${digitalProducts.length} файл бэлэн боллоо. Жагсаалтаас тус бүрд нь татна уу.`}
          </div>
        </div>
      </div>
      {single && firstUrl && (
        <div className="flex flex-col items-start sm:items-end gap-1.5 shrink-0">
          <button onClick={async () => { setDownloading(true); await downloadDigitalFile(firstUrl); setDownloading(false) }} disabled={downloading} className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full text-[13px] font-semibold cursor-pointer transition-opacity hover:opacity-90 disabled:opacity-60 disabled:pointer-events-none" style={{ background: 'white', color: '#0a0a0c' }}>
            {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} {downloading ? 'Татаж байна...' : 'Файл татах'}
          </button>
          {firstStatus && !firstStatus.expired && (
            <div className="text-[11px] tabular-nums" style={{ color: 'rgba(255,255,255,0.55)' }}>
              Үлдсэн: <span className="font-semibold text-white">{firstStatus.downloadsRemaining}/{firstStatus.downloadsMax}</span>
              {firstStatus.expiresAt && <span> · {formatDateNormal(new Date(firstStatus.expiresAt))} хүртэл</span>}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function PaymentSuccessCard() {
  return (
    <div className="rounded-2xl p-5 flex items-center gap-3" style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.3)' }}>
      <div className="w-11 h-11 rounded-full grid place-items-center shrink-0" style={{ background: 'rgba(16,185,129,0.18)', color: '#34d399', border: '1px solid rgba(16,185,129,0.3)' }}>
        <CheckCircle2 className="h-5 w-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[14px] font-semibold text-white">Төлбөр амжилттай төлөгдлөө</div>
        <div className="text-[12px] mt-0.5" style={{ color: 'rgba(255,255,255,0.6)' }}>Захиалга баталгаажиж, бид удахгүй боловсруулж эхэлнэ.</div>
      </div>
    </div>
  )
}

function QpayBlock({ order, vendor, onRefresh }: { order: any, vendor: any, onRefresh: () => void }) {
  const { loading, success, check } = usePaymentChecker({ order, vendor, method: 'qpay', onRefresh })
  const { isInAppBrowser, appLinksBlocked, browserUrl, openInBrowser } = useInAppBrowser({ order })
  if (success) return <PaymentSuccessCard />
  if (!order) return null
  const banks = order?.qpay?.urls || []
  return (
    <div className="rounded-2xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)' }}>
      <MethodCardHeader
        logo={<Image src={qpayLogo} alt="QPay" className="w-full h-full object-contain" />}
        title="QPay"
        subtitle="QR төлбөр"
        badge={<div className="hidden sm:flex items-center gap-1.5 text-[11px] font-medium" style={{ color: '#34d399' }}><Zap className="h-3.5 w-3.5" />Шуурхай</div>}
      />
      <div className="grid sm:grid-cols-[auto_1fr] gap-5 p-5">
        <div className="flex flex-col items-center">
          <QRFrame value={order?.qpay?.QRCode} accent="#ffffff" logoSrc={qpayLogo} size={200} />
          <div className="mt-2 text-[11px] font-semibold text-center" style={{ color: 'rgba(255,255,255,0.55)' }}>
            Захиалга <span className="font-mono text-white">#{order.customId}</span>
          </div>
        </div>
        <div className="flex flex-col">
          <div className="text-[10px] font-semibold uppercase tracking-[0.18em]" style={{ color: '#a8a8b3' }}>Төлөх дүн</div>
          <div className="text-[28px] font-bold tabular-nums tracking-tight text-white mt-1" style={{ fontFamily: 'Gilroy, Inter, sans-serif' }}>{formatPrice(getPayableAmount(order))}</div>
          <div className="mt-4">
            <NumberedSteps
              accent="rgba(255,255,255,0.14)"
              steps={[
                'Банкны аппаа нээгээд QR уншуулах цэс рүү орно уу',
                'Дээрх QR кодыг уншуулан төлбөрөө баталгаажуулна уу',
              ]}
            />
          </div>
          <div className="mt-5"><CheckPaymentButton loading={loading} success={success} onClick={check} /></div>
        </div>
      </div>
      {banks.length > 0 && (
        <div className="px-5 pb-5 sm:hidden">
          <div className="text-[10px] uppercase tracking-[0.18em] font-semibold mb-2" style={{ color: '#a8a8b3' }}>Банкны апп шууд нээх</div>
          {isInAppBrowser && (
            <div className="rounded-xl px-4 py-3.5 mb-3" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.13)' }}>
              <p className="text-[12px]" style={{ color: 'rgba(255,255,255,0.75)' }}>{IN_APP_BROWSER_LABELS.message}</p>
              <a href={browserUrl} onClick={openInBrowser} className="mt-3 inline-flex items-center gap-2 justify-center text-[13px] font-semibold rounded-full py-2.5 px-4" style={{ background: 'white', color: '#0a0a0c' }}>
                {IN_APP_BROWSER_LABELS.action}<ExternalLink className="w-3.5 h-3.5" />
              </a>
              <p className="mt-2.5 text-[11px]" style={{ color: 'rgba(255,255,255,0.55)' }}>{IN_APP_BROWSER_LABELS.hint}</p>
            </div>
          )}
          {!appLinksBlocked && (
            <div className="grid grid-cols-6 gap-2">
              {banks.map((b: any, i: number) => (
                <a key={i} href={b.link} className="aspect-square rounded-lg overflow-hidden transition-colors" style={{ background: 'white', border: '1px solid rgba(255,255,255,0.13)' }}>
                  <img src={b.logo} alt={b.name || `Bank ${i + 1}`} className="w-full h-full object-cover" />
                </a>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function PocketBlock({ order, vendor, onRefresh }: { order: any, vendor: any, onRefresh: () => void }) {
  const { loading, success, check } = usePaymentChecker({ order, vendor, method: 'pocket', onRefresh })
  const accent = '#fb7185'
  if (success) return <PaymentSuccessCard />
  return (
    <div className="rounded-2xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)' }}>
      <MethodCardHeader
        logo={<Image src={pocketzeroLogo} alt="PocketZero" className="w-full h-full object-contain" />}
        title="PocketZero"
        subtitle="Хүүгүй зээл"
        badge={<span className="hidden sm:inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: 'rgba(251,113,133,0.18)', color: accent, border: '1px solid rgba(251,113,133,0.3)' }}>0% ХҮҮТЭЙ</span>}
      />
      <div className="grid sm:grid-cols-[auto_1fr] gap-5 p-5">
        <div className="flex flex-col items-center">
          <QRFrame value={order?.pocket?.qr} accent={accent} logoSrc={pocketzeroLogo} size={200} />
          <div className="mt-2 text-[11px] font-semibold text-center" style={{ color: 'rgba(255,255,255,0.55)' }}>
            Захиалга <span className="font-mono text-white">#{order?.customId}</span>
          </div>
        </div>
        <div className="flex flex-col">
          <div className="text-[10px] font-semibold uppercase tracking-[0.18em]" style={{ color: '#a8a8b3' }}>Зээлээр төлөх</div>
          <div className="text-[28px] font-bold tabular-nums tracking-tight text-white mt-1" style={{ fontFamily: 'Gilroy, Inter, sans-serif' }}>{formatPrice(getPayableAmount(order))}</div>
          <div className="mt-1.5 text-[12px]" style={{ color: 'rgba(255,255,255,0.55)' }}>Сард тэнцүү хуваан төлөх боломжтой. Нэмэлт хүү, шимтгэлгүй.</div>
          <div className="mt-4">
            <NumberedSteps
              accent={accent}
              steps={[
                'Pocket аппыг нээж PocketZero цэс рүү орно уу',
                'Дээрх QR кодыг уншуулж нөхцөлөө баталгаажуулна уу',
              ]}
            />
          </div>
          {order?.pocket?.deeplink && (
            <a
              href={order.pocket.deeplink}
              className="mt-4 lg:hidden inline-flex items-center gap-2 justify-center text-[13px] font-semibold text-white rounded-full py-2.5 px-4 self-start"
              style={{ background: accent }}
            >
              <Image src={pocketzeroLogo} alt="" className="w-4 h-4 object-contain" />Pocket App нээх<ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
          <div className="mt-5"><CheckPaymentButton loading={loading} success={success} onClick={check} /></div>
        </div>
      </div>
    </div>
  )
}

function OmniwayBlock({ order, vendor, onRefresh }: { order: any, vendor: any, onRefresh: () => void }) {
  const { loading, success, check } = usePaymentChecker({ order, vendor, method: 'omniway', onRefresh })
  const accent = '#ffc800'
  if (success) return <PaymentSuccessCard />
  // A cancelled invoice can no longer be paid — never show its stale QR.
  if (order?.omniway?.canceledAt) return null
  return (
    <div className="rounded-2xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)' }}>
      <MethodCardHeader
        logo={<Image src={omniwayLogo} alt="OmniWay" className="w-full h-full object-contain" />}
        title="OmniWay"
        subtitle="Хуваан төлөх"
      />
      <div className="grid sm:grid-cols-[auto_1fr] gap-5 p-5">
        <div className="flex flex-col items-center">
          <QRFrame value={getOmniwayQrImage(order)} accent={accent} size={200} image />
          <div className="mt-2 text-[11px] font-semibold text-center" style={{ color: 'rgba(255,255,255,0.55)' }}>
            Захиалга <span className="font-mono text-white">#{order?.customId}</span>
          </div>
        </div>
        <div className="flex flex-col">
          <div className="text-[10px] font-semibold uppercase tracking-[0.18em]" style={{ color: '#a8a8b3' }}>Зээлээр төлөх</div>
          <div className="text-[28px] font-bold tabular-nums tracking-tight text-white mt-1" style={{ fontFamily: 'Gilroy, Inter, sans-serif' }}>{formatPrice(getPayableAmount(order))}</div>
          <div className="mt-3 text-[13px]" style={{ color: 'rgba(255,255,255,0.75)' }}>OmniWay аппликэйшнээр QR кодыг уншуулж төлнө үү</div>
          {order?.omniway?.qrContent && (
            <a
              href={order.omniway.qrContent}
              className="mt-4 lg:hidden inline-flex items-center gap-2 justify-center text-[13px] font-semibold text-zinc-900 rounded-full py-2.5 px-4 self-start"
              style={{ background: accent }}
            >
              <Image src={omniwayLogo} alt="" className="w-4 h-4 object-contain rounded-sm" />OmniWay-ээр төлөх<ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
          <div className="mt-5"><CheckPaymentButton loading={loading} success={success} onClick={check} /></div>
        </div>
      </div>
    </div>
  )
}

type PhoneMethodConfig = {
  brand: string
  tagline: string
  appName: string
  accent: string
  logo: any
  phone: string
  steps: string[]
}

function PhoneMethodCard({ order, vendor, method, config, onRefresh }: { order: any, vendor: any, method: PaymentEndpoint, config: PhoneMethodConfig, onRefresh: () => void }) {
  const { loading, success, check } = usePaymentChecker({ order, vendor, method, onRefresh })
  if (success) return <PaymentSuccessCard />
  return (
    <div className="rounded-2xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)' }}>
      <MethodCardHeader logo={config.logo} title={config.brand} subtitle={config.tagline} />
      <div className="p-5 grid sm:grid-cols-2 gap-5">
        <div className="flex flex-col">
          <div className="text-[10px] font-semibold uppercase tracking-[0.18em]" style={{ color: '#a8a8b3' }}>Зээлээр төлөх</div>
          <div className="text-[28px] font-bold tabular-nums tracking-tight text-white mt-1" style={{ fontFamily: 'Gilroy, Inter, sans-serif' }}>{formatPrice(getPayableAmount(order))}</div>
          <div className="mt-5 text-[10px] uppercase tracking-[0.18em] font-semibold mb-1.5" style={{ color: '#a8a8b3' }}>Бүртгэлтэй утасны дугаар</div>
          <div className="relative">
            <Phone className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'rgba(255,255,255,0.5)' }} />
            <div className="w-full pl-9 pr-3 py-2.5 rounded-lg text-[13px] font-mono tabular-nums font-semibold text-white" style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.07)' }}>
              {config.phone || '—'}
            </div>
          </div>
          <div className="mt-1.5 text-[11px]" style={{ color: 'rgba(255,255,255,0.5)' }}>{config.appName} апп дээр бүртгэлтэй утасны дугаар.</div>
          <div className="mt-5"><CheckPaymentButton loading={loading} success={success} onClick={check} /></div>
        </div>
        <div className="relative rounded-xl p-5" style={{ background: `linear-gradient(145deg, ${config.accent}1f, ${config.accent}08)`, border: `1px solid ${config.accent}30` }}>
          <div className="text-[10px] font-semibold uppercase tracking-[0.18em] mb-3" style={{ color: config.accent }}>Хэрхэн төлөх</div>
          <NumberedSteps accent={config.accent} steps={config.steps} />
          <div className="mt-4 flex items-center gap-2 text-[11px]" style={{ color: 'rgba(255,255,255,0.55)' }}>
            <Smartphone className="h-3 w-3" style={{ color: config.accent }} />
            Мэдэгдэл таны утсанд хэдхэн секундийн дотор очно.
          </div>
        </div>
      </div>
    </div>
  )
}

function StorepayBlock({ order, vendor, onRefresh }: { order: any, vendor: any, onRefresh: () => void }) {
  return (
    <PhoneMethodCard
      order={order}
      vendor={vendor}
      method="storepay"
      onRefresh={onRefresh}
      config={{
        brand: 'StorePay',
        tagline: 'Хуваан төлөх үйлчилгээ',
        appName: 'StorePay',
        accent: '#38bdf8',
        phone: order?.storepay?.phone,
        logo: <Image src={storepayLogo} alt="StorePay" className="w-full h-full object-contain" />,
        steps: [
          'Бүртгэлтэй утасны дугаар нь мөн эсэхийг шалгах',
          'StorePay апп дээрх ирсэн нэхэмжлэлийн Төлөх товчлуурыг дарна',
          'Хугацаагаа сонгож, зээлээ баталгаажуулна',
        ],
      }}
    />
  )
}

function LendBlock({ order, vendor, onRefresh }: { order: any, vendor: any, onRefresh: () => void }) {
  return (
    <PhoneMethodCard
      order={order}
      vendor={vendor}
      method="lend"
      onRefresh={onRefresh}
      config={{
        brand: 'LendMN',
        tagline: 'Бэлэн мөнгөн зээл',
        appName: 'LendMN',
        accent: '#fb923c',
        phone: order?.lend?.phoneNumber,
        logo: <Image src={lendmnLogo} alt="LendMN" className="w-full h-full object-contain" />,
        steps: [
          'Бүртгэлтэй утасны дугаар нь мөн эсэхийг шалгах',
          'LendMN апп дээрх ирсэн нэхэмжлэлээ шалгана',
          'Хугацаагаа сонгож, зээлээ баталгаажуулна',
        ],
      }}
    />
  )
}

function BankTransferCard({ order }: { order: any }) {
  const bank = order?.vendor?.bank
  const bankName = bank?.bankName || '—'
  const bankIcon = bankList.find(b => b.code == bank?.bankCode)?.icon
  const reference = `#${order?.customId}`
  return (
    <div>
      <div className="rounded-2xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)' }}>
        <div className="px-5 pt-5 pb-4 flex items-center gap-3">
          <div className="w-11 h-11 rounded-lg overflow-hidden grid place-items-center shrink-0" style={{ background: 'white', border: '1px solid rgba(255,255,255,0.13)' }}>
            {bankIcon && <Image src={bankIcon} width={44} height={44} alt={bankName} className="w-full h-full object-cover" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.18em] font-semibold" style={{ color: '#a8a8b3' }}>Хүлээн авагч банк</div>
            <div className="text-[15px] font-semibold text-white truncate" style={{ fontFamily: 'Gilroy, Inter, sans-serif' }}>{bankName}</div>
          </div>
          <div className="hidden sm:flex items-center gap-1.5 text-[11px] font-medium" style={{ color: '#34d399' }}>
            <ShieldCheck className="h-3.5 w-3.5" />Баталгаажсан
          </div>
        </div>
        <div className="px-5 pb-5 grid gap-3">
          <CopyField label="IBAN · Дансны дугаар" value={bank?.accountIban || ''} mono />
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] font-semibold mb-1.5" style={{ color: '#a8a8b3' }}>Хүлээн авагч</div>
              <div className="rounded-lg px-3 py-2.5 text-[13px] font-semibold text-white" style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.07)' }}>{bank?.accountName || '—'}</div>
            </div>
            <CopyField label="Гүйлгээний утга" value={reference} mono />
          </div>
        </div>
        <div className="h-px mx-3" style={{ backgroundImage: 'linear-gradient(to right, rgba(255,255,255,0.13) 50%, transparent 50%)', backgroundSize: '10px 1px', backgroundRepeat: 'repeat-x', backgroundPosition: 'center' }} />
        <div className="px-5 py-5" style={{ background: 'rgba(0,0,0,0.3)' }}>
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.18em] font-semibold mb-1" style={{ color: 'rgba(255,255,255,0.55)' }}>Шилжүүлэх дүн</div>
              <div className="text-[28px] font-bold tabular-nums tracking-tight text-white" style={{ fontFamily: 'Gilroy, Inter, sans-serif' }}>{formatPrice(getPayableAmount(order))}</div>
            </div>
            <div className="text-right shrink-0">
              <div className="text-[10px] uppercase tracking-[0.18em] font-semibold mb-1" style={{ color: 'rgba(255,255,255,0.55)' }}>Захиалга</div>
              <div className="text-[14px] font-mono font-semibold text-white">#{order?.customId}</div>
            </div>
          </div>
        </div>
      </div>
      <div className="mt-3 flex items-start gap-2 px-1 text-[11px]" style={{ color: 'rgba(255,255,255,0.5)' }}>
        <Clock className="h-3 w-3 shrink-0 mt-0.5" />
        <p>
          Гүйлгээний утга хэсэгт <span className="font-semibold text-white">#{order?.customId}</span> захиалгын дугаарыг заавал бичнэ үү. Төлбөр хүлээн авмагц захиалга баталгаажна.
        </p>
      </div>
    </div>
  )
}

function OnDeliveryCard({ order }: { order: any }) {
  return (
    <div className="rounded-2xl p-5 flex items-center gap-4" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.12)' }}>
      <div className="w-10 h-10 rounded-full inline-flex items-center justify-center shrink-0" style={{ background: 'rgba(245,158,11,0.18)', color: '#fbbf24', border: '1px solid rgba(245,158,11,0.3)' }}>
        <HandCoins className="h-5 w-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-white">Барааг авахдаа төлөх</div>
        <div className="text-xs text-white/60 mt-0.5">
          Захиалгын дүн <strong className="text-white/80">{formatPrice(order?.amount)}</strong>-г барааг хүлээн авахдаа төлнө үү. Урьдчилж төлөх шаардлагагүй.
        </div>
      </div>
    </div>
  )
}

function PendingPaymentBlock({ order, vendor, onRefresh }: { order: any, vendor: any, onRefresh: () => void }) {
  /*
    Split payment is checked first, and the order matters: a deposit order flips
    payAfterDelivery on the moment the deposit lands, so the onDelivery branch would
    otherwise swallow the balance state and tell a customer who owes money to "pay at the
    door", with no way to pay online. While the deposit itself is due the flag is still
    false, so it falls through to the rail blocks, which render getPayableAmount.
  */
  const deposit = calcDepositBreakdown(order)
  if (deposit.isAwaitingBalance && !hasLiveInvoice(order)) {
    return <DepositBalanceCard order={order} vendor={vendor} onRefresh={onRefresh} />
  }
  if (order?.payAfterDelivery && !deposit.isDeposit) return <OnDeliveryCard order={order} />
  if (order?.paymentMethod === 'qpay') return <QpayBlock order={order} vendor={vendor} onRefresh={onRefresh} />
  if (order?.paymentMethod === 'storepay') return <StorepayBlock order={order} vendor={vendor} onRefresh={onRefresh} />
  if (order?.paymentMethod === 'pocket') return <PocketBlock order={order} vendor={vendor} onRefresh={onRefresh} />
  if (order?.paymentMethod === 'lend') return <LendBlock order={order} vendor={vendor} onRefresh={onRefresh} />
  // A cancelled OmniWay invoice is not payable — fall through to the fallback cards below.
  if (order?.paymentMethod === 'omniway' && !order?.omniway?.canceledAt) return <OmniwayBlock order={order} vendor={vendor} onRefresh={onRefresh} />
  return <BankTransferCard order={order} />
}

function PrintableReceipt({ order, vendor }: { order: any, vendor: any }) {
  const [mounted, setMounted] = useState(false)
  useEffect(() => { setMounted(true) }, [])
  if (!mounted) return null
  return createPortal(<PrintableReceiptBody order={order} vendor={vendor} />, document.body)
}

function PrintableReceiptBody({ order, vendor }: { order: any, vendor: any }) {
  const { salePriceSavings, discountBreakdown } = calcPriceBreakdown(order)
  const totalDue = (order?.amount || 0) - (order?.totalRefunded || 0)
  const isDelivery = order?.type === 'delivery'
  const orderProducts = order?.products || []
  const allDigital = orderProducts.length > 0 && orderProducts.every((p: any) => (p.product || p)?.isDigital === true)
  const showAddressOrBranch = !allDigital
  const customerName = getCustomerName(order) || '-'
  const addressText = isDelivery
    ? formatOrderAddress(order?.address, order?.channel)
    : (order?.branchData || order?.branch)
      ? `${order?.branchData?.name || order?.branch?.name}, ${order?.branchData?.address || order?.branch?.address}`
      : '-'
  return (
    <div data-print-root className="hidden print:block text-black text-[12px] leading-snug">
      <style>{`@page { size: A4; margin: 16mm; }`}</style>
      <div className="text-center mb-4">
        <div className="text-lg font-bold">{vendor?.name || 'Shop.mn'}</div>
        <div className="text-[11px]">Захиалгын баримт</div>
      </div>
      <div className="flex justify-between border-y border-black py-2 mb-3">
        <div>
          <div className="font-bold">Захиалга #{order?.customId}</div>
          <div className="text-[11px]">{formatDateTimeLong(order?.createdAt)}</div>
        </div>
        <div className="text-right">
          <div className="text-[11px]">Төлөв</div>
          <div className="font-bold">{STATUS_LABELS[order?.status] || '-'}</div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4 mb-3">
        <div>
          <div className="text-[10px] uppercase font-bold mb-1">Хүлээн авагч</div>
          <div>{customerName}</div>
          <div>{order?.phone}{order?.additionalPhone ? ` · ${order.additionalPhone}` : ''}</div>
          {order?.email && <div>{order.email}</div>}
        </div>
        {showAddressOrBranch && (
          <div>
            <div className="text-[10px] uppercase font-bold mb-1">{isDelivery ? 'Хүргэх хаяг' : 'Авах салбар'}</div>
            <div>{addressText}</div>
          </div>
        )}
      </div>
      <table className="w-full border-collapse mb-3">
        <thead>
          <tr className="border-y border-black">
            <th className="text-left py-1 font-bold">Бараа</th>
            <th className="text-right py-1 font-bold w-16">Тоо</th>
            <th className="text-right py-1 font-bold w-24">Үнэ</th>
            <th className="text-right py-1 font-bold w-24">Дүн</th>
          </tr>
        </thead>
        <tbody>
          {orderProducts.map((p: any, i: number) => {
            const productData = p.product || p
            const productName = productData?.name || p?.name || 'Бүтээгдэхүүн'
            const { unitPrice, lineTotal } = getProductPricing(p)
            const qty = p?.quantity || 0
            return (
              <tr key={i} className="border-b border-zinc-300 align-top">
                <td className="py-1 pr-2">
                  {productName}
                  {p.variant?.name && <div className="text-[10px]">{p.variant.name}</div>}
                </td>
                <td className="py-1 text-right tabular-nums">{qty}</td>
                <td className="py-1 text-right tabular-nums">{formatPrice(unitPrice)}</td>
                <td className="py-1 text-right tabular-nums">{formatPrice(lineTotal)}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
      <div className="ml-auto w-1/2 text-[11px]">
        <div className="flex justify-between py-0.5"><span>Бараанын дүн</span><span className="tabular-nums">{formatPrice(order?.subAmount)}</span></div>
        {salePriceSavings > 0 && <div className="flex justify-between py-0.5"><span>Хямдрал</span><span className="tabular-nums">-{formatPrice(salePriceSavings)}</span></div>}
        {discountBreakdown.map(d => (
          <div key={d.id} className="flex justify-between py-0.5"><span>Урамшуулал ({d.name})</span><span className="tabular-nums">-{formatPrice(d.amount)}</span></div>
        ))}
        {(order?.couponAmount || 0) > 0 && (
          <div className="flex justify-between py-0.5"><span>Купон{order?.couponData?.name ? ` (${order.couponData.name})` : ''}</span><span className="tabular-nums">-{formatPrice(order?.couponAmount)}</span></div>
        )}
        {(order?.giftCardAmount || 0) > 0 && (
          <div className="flex justify-between py-0.5"><span>Бэлгийн карт</span><span className="tabular-nums">-{formatPrice(order?.giftCardAmount)}</span></div>
        )}
        {(order?.loyaltyDiscountAmount || 0) > 0 && (
          <div className="flex justify-between py-0.5"><span>Лояалти хөнгөлөлт</span><span className="tabular-nums">-{formatPrice(order?.loyaltyDiscountAmount)}</span></div>
        )}
        {(order?.loyaltyVoucherAmount || 0) > 0 && (
          <div className="flex justify-between py-0.5"><span>Лояалти ваучер</span><span className="tabular-nums">-{formatPrice(order?.loyaltyVoucherAmount)}</span></div>
        )}
        {isDelivery && (
          <div className="flex justify-between py-0.5"><span>Хүргэлт</span><span className="tabular-nums">{vendor?.deliveryType === 'laterDelivery' && order?.deliveryPrice === 0 ? 'Нэмэлтээр тооцно' : order?.deliveryPrice === 0 ? 'Үнэгүй' : formatPrice(order?.deliveryPrice)}</span></div>
        )}
        {(order?.totalRefunded || 0) > 0 && (
          <div className="flex justify-between py-0.5"><span>Буцаалт</span><span className="tabular-nums">-{formatPrice(order?.totalRefunded)}</span></div>
        )}
        <div className="flex justify-between border-t border-black mt-1 pt-1 font-bold text-[13px]">
          <span>Нийт төлөх</span>
          <span className="tabular-nums">{formatPrice(totalDue)}</span>
        </div>
      </div>
      <div className="mt-6 text-center text-[10px]">
        Энэхүү баримтыг {formatDateTimeLong(new Date())}-нд хэвлэв · {vendor?.name || 'Shop.mn'}
      </div>
    </div>
  )
}

function Section({ eyebrow, action, children }: { eyebrow: string, action?: React.ReactNode, children: React.ReactNode }) {
  return (
    <div className="rounded-2xl p-6" style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)' }}>
      <div className="flex items-center justify-between mb-4 gap-3">
        <div className="text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: '#a8a8b3' }}>
          {eyebrow}
        </div>
        {action}
      </div>
      {children}
    </div>
  )
}

function Timeline({ order, vendor, status }: { order: any, vendor: any, status: string }) {
  const terminal = TERMINAL_STATUSES.has(status)
  const isOnDelivery = order?.payAfterDelivery
  const baseOrder = isOnDelivery ? ON_DELIVERY_STATUS_ORDER : STATUS_ORDER
  const stepOrder = showsReadyStep(order, vendor) ? baseOrder : baseOrder.filter(k => k !== 'ready')
  let currentIdx = terminal ? -1 : Math.max(0, stepOrder.indexOf(status))
  if (!terminal && isOnDelivery && order?.paidAt) currentIdx = stepOrder.length - 1

  const steps = stepOrder.map((key, i) => {
    const Icon = STEP_ICONS[key]
    const at = key === 'settled' ? order?.paidAt : (order as any)?.[`${key === 'pending' ? 'created' : key}At`]
    const detail = formatStep(at)
    const done = !terminal && i < currentIdx
    const active = !terminal && i === currentIdx
    return { key, label: STEP_LABELS[key], Icon, detail, done, active }
  })

  if (terminal) {
    const tone = TONES[STATUS_TONE_MAP[status] || 'muted']
    return (
      <div
        className="rounded-2xl p-6 flex items-center gap-4"
        style={{ background: tone.bg, border: `1px solid ${tone.border}` }}
      >
        <div
          className="w-10 h-10 rounded-full inline-flex items-center justify-center shrink-0"
          style={{ background: 'rgba(0,0,0,0.25)', color: tone.color, border: `1px solid ${tone.border}` }}
        >
          <Clock className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[14px] font-semibold" style={{ color: tone.color }}>
            {/* 'pending' reads "Төлбөр хүлээгдэж буй", which is wrong for an order with
                nothing owed up front. See resolveOrderStatus. */}
            {STATUS_LABELS[resolveOrderStatus(order, status)] || 'Тодорхойгүй'}
          </div>
          {(order?.canceledReason || order?.refundedReason || order?.deliveryFailedReason) && (
            <div className="text-[12.5px] mt-1 leading-6" style={{ color: 'rgba(255,255,255,0.6)' }}>
              {order?.canceledReason || order?.refundedReason || order?.deliveryFailedReason}
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="rounded-2xl p-6" style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)' }}>
      <div className="hidden sm:block">
        <div className="flex items-center">
          {steps.map((s, i) => (
            <React.Fragment key={s.key}>
              <span
                className="w-7 h-7 rounded-full inline-flex items-center justify-center text-[10px] font-semibold tabular-nums shrink-0"
                style={{
                  background: s.done ? '#34d399' : s.active ? 'white' : 'rgba(255,255,255,0.035)',
                  color: s.done ? '#063' : s.active ? '#0a0a0c' : 'rgba(255,255,255,0.55)',
                  border: s.done ? '1px solid #34d399' : s.active ? '1px solid white' : '1px solid rgba(255,255,255,0.07)',
                }}
              >
                {s.done ? <CheckCircle2 className="h-3.5 w-3.5" /> : String(i + 1).padStart(2, '0')}
              </span>
              {i < steps.length - 1 && (
                <div
                  className="flex-1 h-px mx-2"
                  style={{ background: s.done ? '#34d399' : 'rgba(255,255,255,0.07)' }}
                />
              )}
            </React.Fragment>
          ))}
        </div>
        <div className="mt-3 grid" style={{ gridTemplateColumns: `repeat(${steps.length}, minmax(0, 1fr))` }}>
          {steps.map((s, i) => (
            <div
              key={s.key}
              className="min-w-0"
              style={{
                textAlign: i === 0 ? 'left' : i === steps.length - 1 ? 'right' : 'center',
              }}
            >
              <div
                className="text-[12px] font-medium leading-tight"
                style={{ color: s.active || s.done ? 'white' : 'rgba(255,255,255,0.55)' }}
              >
                {s.label}
              </div>
              {s.detail && (
                <div className="mt-1 text-[11px] tabular-nums" style={{ color: 'rgba(255,255,255,0.4)' }}>{s.detail}</div>
              )}
            </div>
          ))}
        </div>
      </div>

      <ol className="sm:hidden space-y-4">
        {steps.map((s, i) => (
          <li key={s.key} className="flex gap-3 items-start">
            <div className="flex flex-col items-center shrink-0">
              <span
                className="w-7 h-7 rounded-full inline-flex items-center justify-center text-[10px] font-semibold tabular-nums"
                style={{
                  background: s.done ? '#34d399' : s.active ? 'white' : 'rgba(255,255,255,0.035)',
                  color: s.done ? '#063' : s.active ? '#0a0a0c' : 'rgba(255,255,255,0.55)',
                  border: s.done ? '1px solid #34d399' : s.active ? '1px solid white' : '1px solid rgba(255,255,255,0.07)',
                }}
              >
                {s.done ? <CheckCircle2 className="h-3.5 w-3.5" /> : String(i + 1).padStart(2, '0')}
              </span>
              {i < steps.length - 1 && (
                <span className="w-px flex-1 mt-1 min-h-6" style={{ background: s.done ? '#34d399' : 'rgba(255,255,255,0.07)' }} />
              )}
            </div>
            <div className="flex-1 min-w-0 pb-1">
              <div
                className="text-[13px] font-medium leading-tight"
                style={{ color: s.active || s.done ? 'white' : 'rgba(255,255,255,0.55)' }}
              >
                {s.label}
              </div>
              {s.detail && (
                <div className="mt-0.5 text-[11px] tabular-nums" style={{ color: 'rgba(255,255,255,0.4)' }}>{s.detail}</div>
              )}
            </div>
          </li>
        ))}
      </ol>
    </div>
  )
}

export default function OrderDetail({ order, vendor }: { order: any, vendor: any }) {
  const pathname = usePathname()
  const phones = getVendorPhones(vendor)
  const router = useRouter()

  // Booklo-aware: surface the Booklo delivery status when it's further along than order.status.
  const status = bookloDisplayStatus(order, order?.status)
  const tone = TONES[STATUS_TONE_MAP[status] || 'muted']
  // A pay-after-delivery order stays unpaid all the way through fulfilment, so the
  // "pay on handover" reminder has to survive past 'pending'.
  const isUnsettledOnDelivery = order?.payAfterDelivery && !order?.paidAt && !TERMINAL_STATUSES.has(status)
  const isPending = status === 'pending' || isUnsettledOnDelivery
  const isPickup = order?.type === 'pickup'
  const customerName = getCustomerName(order) || order?.phone || ''
  const orderProducts: any[] = order?.products || []

  const shipping = isPickup
    ? (order?.branchData || order?.branch)
      ? `${order?.branchData?.name || order?.branch?.name}${(order?.branchData?.address || order?.branch?.address) ? `, ${order?.branchData?.address || order?.branch?.address}` : ''}`
      : null
    : formatOrderAddress(order?.address, order?.channel)

  const paymentLabel = PAYMENT_METHOD_LABELS[order?.paymentMethod] || PAYMENT_METHOD_LABELS.transfer
  // paidAt is the only signal for pay-after-delivery — 'delivered' proves nothing there.
  const paymentPaid = order?.payAfterDelivery
    ? !!order?.paidAt
    : (order?.paidAt || ['paid', 'ready', 'shipped', 'delivered'].includes(status))

  const summaryRows = useMemo(() => {
    const fp = (n: any) => String(formatPrice(n) || '0₮')
    const { salePriceSavings, discountBreakdown } = calcPriceBreakdown(order)
    const rows: { label: string, value: string, accent?: string }[] = [
      { label: 'Бараанын дүн', value: fp(order?.subAmount || 0) },
    ]
    if (salePriceSavings > 0) rows.push({ label: 'Хямдрал', value: `-${fp(salePriceSavings)}`, accent: '#34d399' })
    for (const d of discountBreakdown) rows.push({ label: d.name || 'Урамшуулал', value: `-${fp(d.amount)}`, accent: '#34d399' })
    if ((order?.couponAmount || 0) > 0) rows.push({ label: `Купон${order?.couponData?.name ? ` (${order.couponData.name})` : ''}`, value: `-${fp(order.couponAmount)}` })
    if ((order?.giftCardAmount || 0) > 0) rows.push({ label: 'Бэлгийн карт', value: `-${fp(order.giftCardAmount)}` })
    if ((order?.loyaltyDiscountAmount || 0) > 0) rows.push({ label: 'Лояалти хөнгөлөлт', value: `-${fp(order.loyaltyDiscountAmount)}`, accent: '#34d399' })
    if ((order?.loyaltyVoucherAmount || 0) > 0) rows.push({ label: 'Лояалти ваучер', value: `-${fp(order.loyaltyVoucherAmount)}`, accent: '#34d399' })
    if (!isPickup) {
      const later = vendor?.deliveryType === 'laterDelivery' && (order?.deliveryPrice || 0) === 0
      rows.push({ label: 'Хүргэлт', value: later ? 'Нэмэлтээр тооцно' : (order?.deliveryPrice || 0) === 0 ? 'Үнэгүй' : fp(order.deliveryPrice) })
    }
    if ((order?.totalRefunded || 0) > 0) rows.push({ label: 'Буцаагдсан', value: `-${fp(order.totalRefunded)}`, accent: '#fda4af' })
    return rows
  }, [order, isPickup, vendor])

  const totalDue = (order?.amount || 0) - (order?.totalRefunded || 0)
  const depositInfo = calcDepositBreakdown(order)

  return (
    <div>
      <div className="print:hidden">
      <div className="mx-auto w-full max-w-[1280px] px-4 lg:px-8 pt-10 pb-6" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
        <div className="hidden md:flex items-center gap-1.5 text-[12px]" style={{ color: 'rgba(255,255,255,0.55)' }}>
          <a href={navigateUrl('/order', pathname)} className="hover:text-white transition-colors">Захиалга</a>
          <ChevronRight className="h-3 w-3" style={{ opacity: 0.5 }} />
          <span className="text-white font-medium">#{order?.customId}</span>
        </div>
        <div className="mt-4 flex items-end justify-between gap-6 flex-wrap">
          <div className="min-w-0">
            <div className="text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: '#a8a8b3' }}>Захиалга</div>
            <h1
              className="mt-2 font-semibold tracking-[-0.025em] leading-[0.95] text-white"
              style={{ fontFamily: 'Gilroy, Inter, sans-serif', fontSize: 'clamp(28px, 5vw, 44px)' }}
            >
              #{order?.customId}
            </h1>
            <div className="mt-2 text-[13px] tabular-nums" style={{ color: 'rgba(255,255,255,0.55)' }}>
              {formatDateTimeLong(order?.createdAt)}
            </div>
          </div>
          <div
            className="inline-flex items-center gap-2 h-9 px-4 rounded-full text-[12px] font-semibold uppercase tracking-widest"
            style={{ background: tone.bg, color: tone.color, border: `1px solid ${tone.border}` }}
          >
            <span className="inline-flex w-1.5 h-1.5 rounded-full" style={{ background: tone.color }} />
            {STATUS_LABELS[status] || 'Тодорхойгүй'}
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1280px] px-4 lg:px-8 pt-8 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-8">
          <div className="space-y-5 min-w-0">
            {isPending && (
              <>
                <div
                  className="rounded-2xl p-5 flex flex-col sm:flex-row sm:items-start gap-4"
                  style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)' }}
                >
                  <div
                    className="w-10 h-10 rounded-full inline-flex items-center justify-center shrink-0"
                    style={{ background: 'rgba(245,158,11,0.18)', color: '#fbbf24', border: '1px solid rgba(245,158,11,0.3)' }}
                  >
                    <Clock className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[14px] font-semibold text-white">Төлбөр баталгаажихыг хүлээж байна</div>
                    <div className="text-[12.5px] mt-1 leading-6" style={{ color: 'rgba(255,255,255,0.6)' }}>
                      Захиалгаа баталгаажуулахын тулд төлбөрөө гүйцэтгэнэ үү. Шилжүүлгийн утга дээр{' '}
                      <span className="font-mono text-white">#{order?.customId}</span> захиалгын дугаараа бичсэн эсэхээ шалгаарай.
                    </div>
                  </div>
                </div>
                <PendingPaymentBlock order={order} vendor={vendor} onRefresh={() => router.refresh()} />
              </>
            )}

            <Timeline order={order} vendor={vendor} status={status} />

            <DigitalDownloadRow order={order} vendor={vendor} />

            {(order?.loyaltyPointsAwarded || 0) > 0 && (
              <a
                href={navigateUrl('/points', pathname)}
                className="rounded-2xl p-5 flex items-center gap-4 transition-opacity hover:opacity-90"
                style={{ background: 'rgba(168,85,247,0.08)', border: '1px solid rgba(168,85,247,0.3)' }}
              >
                <div
                  className="w-10 h-10 rounded-full inline-flex items-center justify-center shrink-0"
                  style={{ background: 'rgba(168,85,247,0.18)', color: '#d8b4fe', border: '1px solid rgba(168,85,247,0.3)' }}
                >
                  <Coins className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0 text-[13px]" style={{ color: 'rgba(255,255,255,0.6)' }}>
                  <span className="font-semibold text-white tabular-nums">+{formatNumber(order.loyaltyPointsAwarded)} оноо</span> энэ захиалгаас цуглууллаа
                </div>
                <ArrowRight className="h-4 w-4 shrink-0 text-white" />
              </a>
            )}

            <Section eyebrow={`Бараа · ${orderProducts.length}`}>
              <div>
                {orderProducts.map((p: any, i: number) => {
                  const variant = p.variant
                  const image = variant?.images?.[0] || p?.image
                  const isReward = p.isRewardItem
                  const discount = !isReward ? p.discount : null
                  const hasDiscount = discount && (discount.type === 'productDiscount' || discount.type === 'order')
                  const { unitPrice, originalUnitPrice, discountApplied } = getProductPricing(p)
                  const hasDiscountedPrice = originalUnitPrice !== null && unitPrice < originalUnitPrice
                  const isDigital = (p.product || p)?.isDigital
                  const isFullyRefunded = p?.refunded && p?.quantity === 0
                  const isPartiallyRefunded = p?.refunded && p?.quantity > 0
                  const showBadgeRow = isReward || (hasDiscount && discountApplied) || isDigital || isFullyRefunded || isPartiallyRefunded
                  const visibleFields = (p.customFields || p.product?.customFields || []).filter(isCustomFieldVisible)
                  return (
                    <div
                      key={`${p.id}-${variant?.id || i}`}
                      className="flex gap-4 py-4"
                      style={i > 0 ? { borderTop: '1px solid rgba(255,255,255,0.07)' } : {}}
                    >
                      <div className="relative w-16 h-16 shrink-0">
                        <div className="w-full h-full rounded-lg overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.07)' }}>
                          {image && <img src={formatImageURL(image)} alt={p?.name} className="w-full h-full object-cover" />}
                        </div>
                        {isReward && (
                          <div className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full grid place-items-center text-white" style={{ background: '#059669' }}>
                            <Gift className="w-3 h-3" strokeWidth={2.5} />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        {p?.brand?.name && (
                          <div className="text-[10px] font-semibold uppercase tracking-[0.16em] truncate" style={{ color: '#a8a8b3' }}>
                            {p.brand.name}
                          </div>
                        )}
                        <div className="text-[13.5px] font-medium leading-snug mt-0.5 text-white">{p?.name}</div>
                        <div className="text-[11px] mt-1 tabular-nums" style={{ color: 'rgba(255,255,255,0.55)' }}>
                          {variant?.name ? `${variant.name} · ` : ''}{p.quantity || 1} ш.
                        </div>
                        {showBadgeRow && (
                          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                            {isFullyRefunded && (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[11px] font-semibold" style={{ background: 'rgba(244,63,94,0.16)', color: '#fda4af' }}>
                                <RotateCcw className="w-3 h-3" strokeWidth={2.5} />Буцаагдсан
                              </span>
                            )}
                            {isPartiallyRefunded && (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[11px] font-semibold" style={{ background: 'rgba(245,158,11,0.16)', color: '#fbbf24' }}>
                                <RotateCcw className="w-3 h-3" strokeWidth={2.5} />Хэсэгчлэн буцаасан · {p?.refundedQuantity}ш
                              </span>
                            )}
                            {isReward && (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[11px] font-semibold" style={{ background: 'rgba(16,185,129,0.16)', color: '#34d399' }}>
                                <Gift className="w-3 h-3" strokeWidth={2.5} /><span className="line-clamp-1">{p.discountName || 'Бэлэг'}</span>
                              </span>
                            )}
                            {hasDiscount && discountApplied && (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[11px] font-semibold" style={{ background: 'rgba(168,85,247,0.18)', color: '#d8b4fe' }}>
                                <CirclePercent className="w-3 h-3" strokeWidth={2.5} /><span className="line-clamp-1">{discount.name}</span>
                              </span>
                            )}
                            {isDigital && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded-sm text-[11px] font-semibold whitespace-nowrap" style={{ background: 'rgba(59,130,246,0.16)', color: '#93c5fd' }}>Дижитал</span>
                            )}
                          </div>
                        )}
                        {visibleFields.length > 0 && (
                          <div
                            className="mt-2.5 rounded-lg px-3 py-2 space-y-1"
                            style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.07)' }}
                          >
                            {visibleFields.map((field: any, j: number) => (
                              <div key={j} className="flex items-start gap-2 text-[11.5px]">
                                <span className="font-medium shrink-0" style={{ color: 'rgba(255,255,255,0.55)' }}>{field.name}:</span>
                                <span className="text-white min-w-0 wrap-break-word">{renderCustomFieldValue(field, formatDateTimeLong)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        <DigitalDownloadAction product={p} order={order} vendor={vendor} />
                      </div>
                      <div className="text-right shrink-0">
                        {isReward ? (
                          <>
                            <div className="text-[14px] font-semibold tabular-nums" style={{ color: '#34d399' }}>{unitPrice === 0 ? 'Үнэгүй' : formatPrice(unitPrice)}</div>
                            {originalUnitPrice !== null && <div className="text-[11.5px] line-through tabular-nums" style={{ color: 'rgba(255,255,255,0.4)' }}>{formatPrice(originalUnitPrice)}</div>}
                          </>
                        ) : hasDiscountedPrice ? (
                          <>
                            <div className="text-[14px] font-semibold tabular-nums" style={{ color: unitPrice === 0 ? '#34d399' : '#fda4af' }}>{unitPrice === 0 ? 'Үнэгүй' : formatPrice(unitPrice)}</div>
                            <div className="text-[11.5px] line-through tabular-nums" style={{ color: 'rgba(255,255,255,0.4)' }}>{formatPrice(originalUnitPrice)}</div>
                          </>
                        ) : (
                          <div className="text-[14px] font-semibold tabular-nums text-white">{formatPrice(variant?.price || unitPrice || 0)}</div>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            </Section>

            <Section eyebrow={isPickup ? 'Очиж авах салбар' : 'Хүргэлтийн хаяг'}>
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-full inline-flex items-center justify-center shrink-0" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.07)', color: 'white' }}>
                  <MapPin className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  {customerName && <div className="text-[14px] text-white">{customerName}</div>}
                  {order?.phone && (
                    <div className="text-[13px] mt-1 tabular-nums" style={{ color: 'rgba(255,255,255,0.6)' }}>
                      {order.phone}{order?.additionalPhone ? ` · ${order.additionalPhone}` : ''}
                    </div>
                  )}
                  {shipping && (
                    <div className="text-[13px] mt-2 leading-6" style={{ color: 'rgba(255,255,255,0.6)' }}>
                      {shipping}
                    </div>
                  )}
                </div>
              </div>
              {order?.note && (
                <div
                  className="mt-4 rounded-lg p-3 text-[12px] leading-5"
                  style={{ background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.6)' }}
                >
                  <span className="text-white font-medium">Тэмдэглэл — </span>{order.note}
                </div>
              )}
            </Section>

            {(() => {
              const visibleOrderFields = (order?.orderCustomFields || []).filter(isCustomFieldVisible)
              if (visibleOrderFields.length === 0) return null
              return (
                <Section eyebrow="Захиалгын мэдээлэл">
                  <div className="space-y-3">
                    {visibleOrderFields.map((field: any, i: number) => (
                      <div key={i} className="flex items-start gap-3">
                        <div
                          className="w-9 h-9 rounded-full inline-flex items-center justify-center shrink-0"
                          style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.07)', color: 'white' }}
                        >
                          <Info className="h-4 w-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[11px] font-medium uppercase tracking-[0.12em]" style={{ color: 'rgba(255,255,255,0.55)' }}>{field.name}</div>
                          <div className="text-[13.5px] text-white mt-0.5 wrap-break-word">
                            {renderCustomFieldValue(field, formatDateTimeLong)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </Section>
              )
            })()}

            <Section eyebrow="Төлбөр">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[14px] text-white font-medium">{paymentLabel}</div>
                  {order?.paidAt && (
                    <div className="text-[12px] mt-1 tabular-nums" style={{ color: 'rgba(255,255,255,0.55)' }}>
                      {formatDateTimeLong(order.paidAt)}
                    </div>
                  )}
                </div>
                <div
                  className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-widest shrink-0"
                  style={{ color: paymentPaid ? '#34d399' : '#fbbf24' }}
                >
                  <span className="inline-flex w-1.5 h-1.5 rounded-full" style={{ background: paymentPaid ? '#34d399' : '#fbbf24' }} />
                  {paymentPaid ? 'Төлсөн' : 'Төлбөл зохих'}
                </div>
              </div>
            </Section>
          </div>

          <aside className="lg:sticky lg:top-32 self-start space-y-4">
            <div className="rounded-2xl p-6" style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)' }}>
              <div className="text-[10px] font-semibold uppercase tracking-[0.22em] mb-4" style={{ color: '#a8a8b3' }}>Хураангуй</div>
              <div className="space-y-2.5 text-[13px]">
                {summaryRows.map(r => (
                  <div key={r.label} className="flex justify-between gap-3">
                    <span style={{ color: 'rgba(255,255,255,0.6)' }}>{r.label}</span>
                    <span className="tabular-nums truncate" style={{ color: r.accent || 'white' }}>{r.value}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-4 flex items-baseline justify-between" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
                <span className="text-[13px] text-white">Нийт</span>
                <span
                  className="text-[22px] font-semibold tabular-nums text-white"
                  style={{ fontFamily: 'Gilroy, Inter, sans-serif' }}
                >
                  {formatPrice(totalDue)}
                </span>
              </div>
              {/* Split payment: how the total divides. Below the grand total, never in
                  place of it — this is a split of what is owed, not a change to it. */}
              {depositInfo.isDeposit && (
                <div className="mt-3 pt-3 flex flex-col gap-2 text-[13px]" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
                  <div className="flex items-baseline justify-between">
                    <span className="text-white/60">{DEPOSIT_LABELS.deposit}{depositInfo.isDepositPaid ? ' · төлсөн' : ' · хүлээгдэж буй'}</span>
                    <span className={'tabular-nums ' + (depositInfo.isDepositPaid ? 'text-emerald-400' : 'text-white/80')}>{formatPrice(depositInfo.depositAmount)}</span>
                  </div>
                  <div className="flex items-baseline justify-between">
                    <span className={'flex items-center gap-2 ' + (depositInfo.outstanding > 0 ? 'text-amber-300 font-semibold' : 'text-white/60')}>
                      {depositInfo.outstanding > 0 ? DEPOSIT_LABELS.balanceDue : DEPOSIT_LABELS.balance}
                      {depositInfo.isFullyPaid && <span className="text-[11px] text-emerald-400">Төлсөн</span>}
                    </span>
                    <span className={'tabular-nums font-semibold ' + (depositInfo.isFullyPaid ? 'text-emerald-400' : depositInfo.outstanding > 0 ? 'text-amber-300' : 'text-white/60')}>
                      {formatPrice(depositInfo.balanceAmount) || '0₮'}
                    </span>
                  </div>
                  {order?.depositTerms && (
                    /* Vendor-authored plain text — rendered as text, never as HTML. */
                    <p className="text-[11px] text-white/45 whitespace-pre-line">{order.depositTerms}</p>
                  )}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <button
                onClick={() => router.push(navigateUrl('/product', pathname))}
                className="w-full h-12 rounded-full text-[13.5px] font-medium inline-flex items-center justify-center gap-2 transition-colors hover:bg-white/10"
                style={{ background: 'rgba(255,255,255,0.035)', color: 'white', border: '1px solid rgba(255,255,255,0.07)' }}
              >
                Дахин дэлгүүр хэсэх
              </button>
            </div>

            {(vendor?.email || phones.length > 0) && (
              <div className="text-[11px] text-center" style={{ color: 'rgba(255,255,255,0.4)' }}>
                Тусламж хэрэгтэй бол{' '}
                {vendor?.email && <a href={`mailto:${vendor.email}`} className="text-white underline underline-offset-2">{vendor.email}</a>}
                {vendor?.email && phones.length > 0 && ' эсвэл '}
                {phones.length > 0 && <>{phones.map((p, i) => (<span key={p}>{i > 0 && ', '}<a href={`tel:${p}`} className="text-white">{p}</a></span>))}</>}
                {' '}руу хандаарай.
              </div>
            )}

            <button
              type="button"
              onClick={() => window.print()}
              className="w-full inline-flex items-center justify-center gap-1.5 text-[11px] font-semibold cursor-pointer transition-colors hover:text-white hover:bg-white/5 rounded-full py-2 -my-2"
              style={{ color: 'rgba(255,255,255,0.55)' }}
            >
              <Printer className="h-3 w-3" /> Баримт хэвлэх
            </button>
          </aside>
        </div>
      </div>
      </div>

      <PrintableReceipt order={order} vendor={vendor} />
    </div>
  )
}
